import { IBridgeLoanGridRecord } from '@apply/api/src/api/loanApi';
import { LenderToRefinance } from '@apply/constants';
import { formatTerm } from '../../../../../common/functions/formatters/formatTerm';
import { repaidLoanFormats as formats } from '../formatters/repaidLoanFormats';

type Loan = IBridgeLoanGridRecord;
type LoanValue = (loan: Loan) => number | null;
type Format = (value: number | null) => string;

export interface IRepaidLoanSummaryItem {
    name: string; // Unique per item; used by calculateCustomSummary
    column: string; // The column (dataField or name) the item shows in
    text: (loans: Loan[]) => string;
    cssClass?: string;
}

const presentValues = (loans: Loan[], value: LoanValue): number[] => loans.map(value).filter((v): v is number => v != null);

const sum = (loans: Loan[], value: LoanValue): number | null => {
    const values = presentValues(loans, value);
    return values.length > 0 ? values.reduce((total, v) => total + v, 0) : null;
};

// The mean of the loans that have a value
const mean = (loans: Loan[], value: LoanValue): number | null => {
    const values = presentValues(loans, value);
    return values.length > 0 ? values.reduce((total, v) => total + v, 0) / values.length : null;
};

// The Size-weighted mean of the loans that have both a value and a Size.
const sizeWeightedMean = (loans: Loan[], value: LoanValue): number | null => {
    let weightedTotal = 0;
    let totalSize = 0;
    for (const loan of loans) {
        const v = value(loan);
        if (v != null && loan.size) {
            weightedTotal += v * loan.size;
            totalSize += loan.size;
        }
    }
    return totalSize > 0 ? weightedTotal / totalSize : null;
};

// The loans' total Size over their total value
const totalLtv = (loans: Loan[], value: LoanValue): number | null => {
    let totalSize = 0;
    let totalValue = 0;
    for (const loan of loans) {
        const v = value(loan);
        if (v != null && loan.size != null) {
            totalSize += loan.size;
            totalValue += v;
        }
    }
    return totalValue > 0 ? totalSize / totalValue : null;
};

// The value behind each LTV, for the loans that have the LTV (one needs a value for every property)
const underwrittenLtvValue: LoanValue = (loan) => (loan.underwrittenLtv != null ? loan.underwrittenValue : null);
const appraisedLtvValue: LoanValue = (loan) => (loan.appraisedLtv != null ? loan.appraisedValue : null);

const label = (column: string, text: string): IRepaidLoanSummaryItem => ({
    name: `${column}_${text}`,
    column,
    text: () => text,
});

// Holds a row's place in a column that has only a total or only an average (a lone item would center between the rows)
const blank = (column: string): IRepaidLoanSummaryItem => ({
    name: `${column}_blank`,
    column,
    text: () => '',
});

const totalAndMean = (column: string, value: LoanValue, format: Format): IRepaidLoanSummaryItem[] => [
    { name: `${column}_total`, column, text: (loans) => format(sum(loans, value)) },
    { name: `${column}_mean`, column, text: (loans) => format(mean(loans, value)) },
];

const weightedMean = (column: string, value: LoanValue, format: Format): IRepaidLoanSummaryItem[] => [
    blank(column),
    { name: `${column}_weightedMean`, column, text: (loans) => format(sizeWeightedMean(loans, value)) },
];

const formatUnits: Format = (value) => (value == null ? '' : Number(value.toFixed(2)).toString());

// Footer: a Totals row, then an averages row (a column's items stack in the order listed)
export const repaidLoanSummaryItems: IRepaidLoanSummaryItem[] = [
    label('bfiNumber', 'Total'),
    label('bfiNumber', 'Average'),
    { name: 'borrower_loanCount', column: 'borrower', text: (loans) => `${loans.length} Loans` },
    blank('borrower'),
    ...totalAndMean('units', (loan) => loan.units, formatUnits),
    ...totalAndMean('size', (loan) => loan.size, formats.currency),
    ...weightedMean('oneYearTreasuryRate', (loan) => loan.oneYearTreasuryRate, formats.oneYearTreasuryRate),
    ...weightedMean('estimatedRate', (loan) => loan.estimatedRate, formats.rate),
    ...weightedMean('adjustmentRate', (loan) => loan.adjustmentRate, formats.adjustmentRate),
    ...weightedMean('servicingRate', (loan) => loan.servicingRate, formats.servicingRate),
    blank('term'),
    {
        name: 'term_weightedMean',
        column: 'term',
        text: (loans) =>
            formatTerm(
                sizeWeightedMean(loans, (loan) => loan.initialTermMonths),
                sizeWeightedMean(loans, (loan) => loan.contractualExtension1Months),
                sizeWeightedMean(loans, (loan) => loan.contractualExtension2Months)
            ),
    },
    blank('underwrittenLtv'),
    {
        name: 'underwrittenLtv_totalLtv',
        column: 'underwrittenLtv',
        text: (loans) => formats.ltv(totalLtv(loans, (loan) => underwrittenLtvValue(loan) ?? appraisedLtvValue(loan))),
        cssClass: 'underwrittenLtvAverage',
    },
    blank('appraisedLtv'),
    { name: 'appraisedLtv_totalLtv', column: 'appraisedLtv', text: (loans) => formats.ltv(totalLtv(loans, appraisedLtvValue)) },
    ...totalAndMean('appraisedValue', (loan) => loan.appraisedValue, formats.currency),
    ...totalAndMean('cashOut', (loan) => loan.cashOut, formats.currency),
    ...weightedMean('creditScoreRate', (loan) => loan.creditScoreRate, formats.credit),
    {
        name: 'refinanceLender_bfiCount',
        column: 'refinanceLender',
        text: (loans) => `BFI: ${loans.filter((loan) => loan.lenderToRefinanceId === LenderToRefinance.Bfi).length}`,
    },
    blank('refinanceLender'),
];

export const underwrittenLtvAverageHint = 'For all loans. Based on LTVuw, or if no LTVuw, the LTVa';
